import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "./api-client";

export function useGetAllUsers() {
  return useMutation({
    mutationFn: ({ page, limit, role, status, isActive, fieldStatus, search }) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit)
      });

      if (role) {
        params.append("role", role);
      }

      // Always send status parameter (default to 'all' if not provided)
      params.append("status", status || "all");

      if (isActive) {
        params.append("isActive", isActive);
      }

      if (fieldStatus) {
        params.append("fieldStatus", fieldStatus);
      }

      if (search) {
        params.append("search", search);
      }

      return apiClient({
        url: `admin/users?${params.toString()}`,
        method: "GET"
      });
    }
  });
}

export function useUserDetails() {
  return useMutation({
    mutationFn: (slug) => apiClient({ url: "admin/user/details/" + slug, method: "GET" })
  });
}
export function useCreateUser() {
  return useMutation({
    mutationFn: ({ payload, data, fieldStatus }) => {
      const params = {};
      if (fieldStatus) {
        params.fieldStatus = fieldStatus;
      }
      return apiClient({
        url: "admin/createUser",
        method: "POST",
        data: data ?? payload,
        params
      });
    }
  });
}

export function useUpdateClientUser() {
  return useMutation({
    mutationFn: (payload) => apiClient({ url: "users/client", method: "PATCH", data: payload })
  });
}
export function useUpdateWorkerUser() {
  return useMutation({
    mutationFn: (payload) => apiClient({ url: "users/worker", method: "PATCH", data: payload })
  });
}

export function useUserMe(options) {
  return useQuery({
    queryKey: ["userMe"],
    queryFn: () => apiClient({ url: "users/me", method: "GET" }),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options
  });
}

export function useUpdateClientByAdmin() {
  return useMutation({
    mutationFn: ({ payload, fieldStatus }) => {
      const params = {};
      if (fieldStatus) {
        params.fieldStatus = fieldStatus;
      }
      return apiClient({
        url: "admin/user/update/" + payload.slug,
        method: "PATCH",
        data: payload,
        params
      });
    }
  });
}

export function useUpdateWorkerByAdmin() {
  return useMutation({
    mutationFn: ({ slug, payload, data, fieldStatus }) => {
      const params = {};
      if (fieldStatus) {
        params.fieldStatus = fieldStatus;
      }
      const requestSlug = slug ?? payload?.slug;
      return apiClient({
        url: "admin/user/update/" + requestSlug,
        method: "PATCH",
        data: data ?? payload,
        params
      });
    }
  });
}

export function useGetAllSubAdmins() {
  return useMutation({
    mutationFn: ({ page, limit }) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit)
      });

      return apiClient({
        url: `admin/sub-admins/all?${params.toString()}`,
        method: "GET"
      });
    }
  });
}

export function useBlockUnblockUser() {
  return useMutation({
    mutationFn: ({ slug, status }) => {
      return apiClient({
        url: `admin/blockUnblockUser/${slug}`,
        method: "PATCH",
        data: { status }
      });
    }
  });
}

export function useCreateSubAdmin() {
  return useMutation({
    mutationFn: (payload) => {
      return apiClient({
        url: "admin/createSubAdmin",
        method: "POST",
        data: payload
      });
    }
  });
}

export function useUpdateSubAdmin() {
  return useMutation({
    mutationFn: ({ slug, payload }) => {
      return apiClient({
        url: `admin/updateSubAdmin/${slug}`,
        method: "PATCH",
        data: payload
      });
    }
  });
}

export function useUpdateSubAdminProfile() {
  return useMutation({
    mutationFn: (payload) => {
      return apiClient({
        url: "users/sub-admin",
        method: "PATCH",
        data: payload
      });
    }
  });
}

export function useGetAuditLogs() {
  return useMutation({
    mutationFn: ({ page, limit, startDate, endDate, search, userRole }) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit)
      });

      if (startDate) {
        params.append("startDate", startDate);
      }
      if (endDate) {
        params.append("endDate", endDate);
      }
      if (search) {
        params.append("search", search);
      }
      if (userRole) {
        params.append("userRole", userRole);
      }

      return apiClient({
        url: `admin/audit-logs?${params.toString()}`,
        method: "GET"
      });
    }
  });
}

export function useUpdateAuditLog() {
  return useMutation({
    mutationFn: ({ id, description }) => {
      return apiClient({
        url: `admin/audit-logs/update/${id}`,
        method: "PATCH",
        data: { description }
      });
    }
  });
}
